/**
 * Inactivity Timeout Hook
 *
 * Tracks user activity and triggers a warning/logout after periods of inactivity.
 * Security feature to prevent unauthorized access on unattended workstations.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseInactivityTimeoutOptions {
  /** Time in milliseconds before showing warning (default: 15 minutes) */
  warningTimeout?: number;
  /** Time in milliseconds after warning before auto-logout (default: 1 minute) */
  logoutTimeout?: number;
  /** Callback when user should be logged out */
  onLogout: () => void;
  /** Whether the hook is active (only track when authenticated) */
  isActive?: boolean;
}

interface UseInactivityTimeoutReturn {
  /** Whether the warning modal should be shown */
  showWarning: boolean;
  /** Seconds remaining until auto-logout */
  secondsRemaining: number;
  /** Reset the inactivity timer (e.g., when user confirms they're still active) */
  resetTimer: () => void;
  /** Dismiss warning and reset timer */
  dismissWarning: () => void;
}

const DEFAULT_WARNING_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const DEFAULT_LOGOUT_TIMEOUT = 60 * 1000; // 1 minute after warning

export function useInactivityTimeout({
  warningTimeout = DEFAULT_WARNING_TIMEOUT,
  logoutTimeout = DEFAULT_LOGOUT_TIMEOUT,
  onLogout,
  isActive = true,
}: UseInactivityTimeoutOptions): UseInactivityTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.floor(logoutTimeout / 1000));

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start the logout countdown
  const startLogoutCountdown = useCallback(() => {
    setShowWarning(true);
    setSecondsRemaining(Math.floor(logoutTimeout / 1000));

    // Start countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Set logout timer
    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      setShowWarning(false);
      onLogout();
    }, logoutTimeout);
  }, [logoutTimeout, onLogout, clearAllTimers]);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    clearAllTimers();
    setShowWarning(false);
    setSecondsRemaining(Math.floor(logoutTimeout / 1000));

    if (!isActive) return;

    // Start warning timer
    warningTimerRef.current = setTimeout(() => {
      startLogoutCountdown();
    }, warningTimeout);
  }, [warningTimeout, logoutTimeout, isActive, clearAllTimers, startLogoutCountdown]);

  // Dismiss warning and reset
  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Don't reset if warning is already showing - user must explicitly dismiss
    if (showWarning) return;
    resetTimer();
  }, [showWarning, resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!isActive) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity handler to avoid excessive calls
    let lastHandled = 0;
    const throttledHandler = () => {
      const now = Date.now();
      if (now - lastHandled > 1000) { // Throttle to once per second
        lastHandled = now;
        handleActivity();
      }
    };

    // Add listeners
    events.forEach((event) => {
      document.addEventListener(event, throttledHandler, { passive: true });
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledHandler);
      });
      clearAllTimers();
    };
  }, [isActive, handleActivity, resetTimer, clearAllTimers]);

  return {
    showWarning,
    secondsRemaining,
    resetTimer,
    dismissWarning,
  };
}
